import React, { useState, useEffect } from "react";
import { fetchWrapper } from "src/helpers";
import { alertService } from "src/services";
import { PortfolioFolder } from "./PortfolioFolder";

interface PortfolioManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  token?: string;
  editingFolder: PortfolioFolder | null;
  onSuccess: (folder: PortfolioFolder, isEdit: boolean) => void;
}

const PortfolioManagerModal = ({
  isOpen,
  onClose,
  token,
  editingFolder,
  onSuccess,
}: PortfolioManagerModalProps) => {
  const [newPortfolioName, setNewPortfolioName] = useState("");
  const [newInvestment, setNewInvestment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [investmentError, setInvestmentError] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (editingFolder) {
        setNewPortfolioName(editingFolder.name);
        setNewInvestment(
          editingFolder.investment ? String(editingFolder.investment) : "",
        );
      } else {
        setNewPortfolioName("");
        setNewInvestment(
          process.env.NEXT_PUBLIC_DEFAULT_INVESTMENT || "1000000",
        );
      }
    }
  }, [isOpen, editingFolder]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900/90 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingFolder ? "Edit Portfolio" : "Add Portfolio"}
            </h3>
            <button
              onClick={() => {
                onClose();
                setInvestmentError("");
              }}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer"
            >
              ✕
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Portfolio Name
            </label>
            <input
              value={newPortfolioName}
              onChange={(e) => setNewPortfolioName(e.target.value)}
              maxLength={20}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1E1E2D] text-gray-900 dark:text-white px-3 py-2 text-sm"
              placeholder="Enter portfolio name"
            />
          </div>
          {!editingFolder && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Investment
              </label>
              <input
                value={newInvestment}
                onChange={(e) => {
                  let val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    if (val.length > 12) {
                      val = val.substring(0, 12);
                    }

                    const normalized = val.replace(/^0+(?!$)/, "");
                    setNewInvestment(normalized);
                    if (Number(val) <= 0) {
                      setInvestmentError("Investment should be greater than 0");
                    } else {
                      setInvestmentError("");
                    }
                  }
                }}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1E1E2D] text-gray-900 dark:text-white px-3 py-2 text-sm"
                placeholder="0.00"
              />

              {investmentError && (
                <p className="text-red-500 text-xs mt-1">{investmentError}</p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                onClose();
                setInvestmentError("");
              }}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={async () => {
                const name = newPortfolioName.trim().replace(/\s+/g, " ");
                if (!name) {
                  alertService.error("Portfolio name is required");
                  return;
                }
                if (!token) {
                  alertService.error("User token missing");
                  return;
                }

                try {
                  setSubmitting(true);

                  if (editingFolder) {
                    const body = { name };
                    const response = await fetchWrapper.put(
                      `/api/users/portfolio/folders/${editingFolder.id}`,
                      body,
                      { Authorization: `Bearer ${token}` },
                    );

                    if (response?.success) {
                      onSuccess({ ...editingFolder, name }, true);
                      alertService.success("Portfolio updated");
                      onClose();
                    } else {
                      alertService.error(
                        response?.message || "Failed to update portfolio",
                      );
                    }
                  } else {
                    const investmentValue = newInvestment
                      ? Number(newInvestment)
                      : 0;

                    if (investmentValue <= 0) {
                      setInvestmentError("Investment should be greater than 0");
                      return;
                    }

                    const body = {
                      name,
                      investment: investmentValue,
                    };
                    const response = await fetchWrapper.post(
                      "/api/users/portfolio/folders/create",
                      body,
                      { Authorization: `Bearer ${token}` },
                    );
                    if (response?.success && response.portfolio) {
                      onSuccess(
                        {
                          id: response.portfolio.id,
                          name: response.portfolio.name,
                          investment: Number(
                            response.portfolio.investment ?? 0,
                          ),
                        },
                        false,
                      );
                      alertService.success("Portfolio created");
                      onClose();
                    } else {
                      alertService.error(
                        response?.message || "Failed to create portfolio",
                      );
                    }
                  }
                } catch (error) {
                  console.error(error);
                  alertService.error(
                    editingFolder
                      ? "Failed to update portfolio"
                      : "Failed to create portfolio",
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
              className="px-4 py-2 text-sm rounded-md bg-[#00A85F] hover:bg-[#009952] text-white disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
            >
              {submitting ? "Saving..." : editingFolder ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioManagerModal;
